import {
  AgeGroup,
  AttendanceFrequencyResponse,
  FrequencyTypeEnum,
  GetTimetablesResponse,
  ResponsibleTypeEnum,
  SchoolLevel,
  Timetable,
} from '@schooly/api';

import {
  getTimetableWarningItems,
  TimetableWarningItem,
} from '../../SchoolGeneral/SchoolGeneralAgeGroups/am-pm-helpers';

type TimetableStructureSnapshot = GetTimetablesResponse['structures'][number];

export type TimetableRef = { id: string; name: string };

export type TimetableGroupEntry = { label: string; timetables: TimetableRef[] };

// Which register(s) a group is missing from — driven by the session(s) whose
// responsible is Subject teacher, plus per-period when enabled (each requires a
// published timetable).
export type { TimetableWarningItem };

export type TimetableCellContent =
  | { kind: 'not_required' }
  | { kind: 'uniform'; timetables: TimetableRef[] }
  | { kind: 'per_group'; entries: TimetableGroupEntry[] }
  | {
      kind: 'warning';
      registers: TimetableWarningItem[];
      missingLabels: string[];
      coveredEntries: TimetableGroupEntry[];
    };

export interface AttendanceFrequencyRowData {
  key: string;
  displayLabels: string[];
  amResponsible: ResponsibleTypeEnum;
  pmResponsible: ResponsibleTypeEnum | null;
  perPeriodEnabled: boolean;
  timetable: TimetableCellContent;
}

// A level is collapsed to its name (once, at its first member's order) only when every
// active age group of that level is present in the row; otherwise age groups stay individual.
export const collapseAgeGroupsByLevel = (
  rowAgeGroups: AgeGroup[],
  allActiveAgeGroups: AgeGroup[],
  schoolLevels: SchoolLevel[],
): string[] => {
  const levelNameById = new Map(schoolLevels.map((level) => [level.id, level.name]));

  const totalByLevel = new Map<string, number>();
  for (const ageGroup of allActiveAgeGroups) {
    if (ageGroup.level_id) {
      totalByLevel.set(ageGroup.level_id, (totalByLevel.get(ageGroup.level_id) ?? 0) + 1);
    }
  }

  const rowCountByLevel = new Map<string, number>();
  for (const ageGroup of rowAgeGroups) {
    if (ageGroup.level_id) {
      rowCountByLevel.set(ageGroup.level_id, (rowCountByLevel.get(ageGroup.level_id) ?? 0) + 1);
    }
  }

  const isFullyCovered = (levelId: string): boolean =>
    levelNameById.has(levelId) &&
    (totalByLevel.get(levelId) ?? 0) > 0 &&
    rowCountByLevel.get(levelId) === totalByLevel.get(levelId);

  const labels: string[] = [];
  const emittedLevels = new Set<string>();

  for (const ageGroup of [...rowAgeGroups].sort((a, b) => a.order - b.order)) {
    const levelId = ageGroup.level_id;
    if (levelId && isFullyCovered(levelId)) {
      if (!emittedLevels.has(levelId)) {
        labels.push(levelNameById.get(levelId) as string);
        emittedLevels.add(levelId);
      }
      continue;
    }
    labels.push(ageGroup.name);
  }

  return labels;
};

interface FrequencyGroup {
  key: string;
  amResponsible: ResponsibleTypeEnum;
  pmResponsible: ResponsibleTypeEnum | null;
  perPeriodEnabled: boolean;
  // Which registers depend on a published timetable — see getTimetableWarningItems.
  registers: TimetableWarningItem[];
  ageGroups: AgeGroup[];
  order: number;
}

const getPmResponsible = (frequency: AttendanceFrequencyResponse): ResponsibleTypeEnum | null => {
  if (frequency.frequency !== FrequencyTypeEnum.AmPm) return null;
  return frequency.pm_responsible ?? null;
};

const groupByFrequency = (ageGroups: AgeGroup[]): FrequencyGroup[] => {
  const groups = new Map<string, FrequencyGroup>();
  const ordered = [...ageGroups].sort((a, b) => a.order - b.order);

  for (const ageGroup of ordered) {
    const frequency = ageGroup.attendance_frequency;
    if (!frequency) continue;

    const amResponsible = frequency.am_responsible;
    const pmResponsible = getPmResponsible(frequency);
    const perPeriodEnabled = frequency.per_period_enabled ?? false;
    const key = `${amResponsible}|${pmResponsible ?? 'none'}|${perPeriodEnabled ? 'pp' : 'no-pp'}`;

    const existing = groups.get(key);
    if (existing) {
      existing.ageGroups.push(ageGroup);
      existing.order = Math.min(existing.order, ageGroup.order);
    } else {
      groups.set(key, {
        key,
        amResponsible,
        pmResponsible,
        perPeriodEnabled,
        registers: getTimetableWarningItems(frequency),
        ageGroups: [ageGroup],
        order: ageGroup.order,
      });
    }
  }

  return [...groups.values()].sort((a, b) => a.order - b.order);
};

const buildTimetableRefs = (
  ageGroup: AgeGroup,
  published: Timetable[],
  structureById: Map<string, TimetableStructureSnapshot>,
): TimetableRef[] => {
  const distinct = new Map<string, TimetableRef>();
  for (const timetable of published) {
    if (structureById.get(timetable.structure_id)?.age_group_ids.includes(ageGroup.id)) {
      distinct.set(timetable.id, { id: timetable.id, name: timetable.name });
    }
  }
  return [...distinct.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const setKeyOf = (refs: TimetableRef[]): string =>
  refs
    .map((ref) => ref.id)
    .sort()
    .join('|');

interface AgeGroupTimetables {
  ageGroup: AgeGroup;
  timetables: TimetableRef[];
  setKey: string;
}

const groupBySetKey = (
  items: AgeGroupTimetables[],
  allActiveAgeGroups: AgeGroup[],
  schoolLevels: SchoolLevel[],
): TimetableGroupEntry[] => {
  const bySetKey = new Map<string, AgeGroupTimetables[]>();
  for (const item of items) {
    const existing = bySetKey.get(item.setKey);
    if (existing) existing.push(item);
    else bySetKey.set(item.setKey, [item]);
  }

  return [...bySetKey.values()]
    .map((members) => {
      const ageGroups = members.map((member) => member.ageGroup);
      return {
        label: collapseAgeGroupsByLevel(ageGroups, allActiveAgeGroups, schoolLevels).join(', '),
        timetables: members[0].timetables,
        order: Math.min(...ageGroups.map((ageGroup) => ageGroup.order)),
      };
    })
    .sort((a, b) => a.order - b.order)
    .map(({ label, timetables }) => ({ label, timetables }));
};

const resolveTimetableCell = (
  group: FrequencyGroup,
  timetables: Timetable[],
  structures: TimetableStructureSnapshot[],
  schoolLevels: SchoolLevel[],
  allActiveAgeGroups: AgeGroup[],
): TimetableCellContent => {
  const { registers } = group;

  if (registers.length === 0) return { kind: 'not_required' };

  const structureById = new Map(structures.map((structure) => [structure.id, structure]));
  const published = timetables.filter((timetable) => timetable.published_at != null);

  const items: AgeGroupTimetables[] = group.ageGroups.map((ageGroup) => {
    const refs = buildTimetableRefs(ageGroup, published, structureById);
    return { ageGroup, timetables: refs, setKey: setKeyOf(refs) };
  });

  const missing = items.filter((item) => item.timetables.length === 0);

  if (missing.length > 0) {
    const covered = items.filter((item) => item.timetables.length > 0);
    return {
      kind: 'warning',
      registers,
      missingLabels: collapseAgeGroupsByLevel(
        missing.map((item) => item.ageGroup),
        allActiveAgeGroups,
        schoolLevels,
      ),
      coveredEntries: groupBySetKey(covered, allActiveAgeGroups, schoolLevels),
    };
  }

  const uniqueSetKeys = new Set(items.map((item) => item.setKey));
  if (uniqueSetKeys.size === 1) {
    return { kind: 'uniform', timetables: items[0].timetables };
  }

  return { kind: 'per_group', entries: groupBySetKey(items, allActiveAgeGroups, schoolLevels) };
};

const isUnconfigured = (ageGroup: AgeGroup): boolean =>
  !ageGroup.archived && ageGroup.attendance_frequency == null;

export const buildAttendanceFrequencyRows = (
  ageGroups: AgeGroup[],
  timetables: Timetable[],
  structures: TimetableStructureSnapshot[],
  schoolLevels: SchoolLevel[],
): {
  configuredRows: AttendanceFrequencyRowData[];
  unconfiguredAgeGroups: AgeGroup[];
  unconfiguredLabels: string[];
} => {
  const configuredRows = groupByFrequency(ageGroups).map((group) => ({
    key: group.key,
    displayLabels: collapseAgeGroupsByLevel(group.ageGroups, ageGroups, schoolLevels),
    amResponsible: group.amResponsible,
    pmResponsible: group.pmResponsible,
    perPeriodEnabled: group.perPeriodEnabled,
    timetable: resolveTimetableCell(group, timetables, structures, schoolLevels, ageGroups),
  }));

  const unconfiguredAgeGroups = [...ageGroups]
    .filter(isUnconfigured)
    .sort((a, b) => a.order - b.order);

  const unconfiguredLabels = collapseAgeGroupsByLevel(
    unconfiguredAgeGroups,
    ageGroups,
    schoolLevels,
  );

  return { configuredRows, unconfiguredAgeGroups, unconfiguredLabels };
};
